/**
 * Plain text read as a DOCUMENT, not as subtitles (SEO_BRIEF_08 section 7.2): /txt-to-pdf and
 * /text-to-pdf.
 *
 * The TXT subtitle reader (`src/formats/parse/txt.ts`) answers a different question. It reads a
 * `.txt` as a transcript: lines led by a time keep that time, and prose is timed from reading
 * speed, one subtitle per line. Through the PDF writer's default layout that gives a number and an
 * invented time on every line, which is the opposite of what someone converting a letter wants.
 *
 * So these pages skip it and read the text as it is written:
 *
 *   • A BLANK LINE (or a run of them, or a line of only spaces) separates paragraphs.
 *   • A SINGLE LINE BREAK stays a line break: each source line is its own line in the paragraph,
 *     and the writer wraps a long one at the page width rather than splitting it into pieces.
 *   • Nothing is timed, numbered, joined, cut or re-ordered. Each paragraph becomes one cue so the
 *     PDF writer can lay it out; the cue's time is a placeholder no layout here ever prints,
 *     because these pages write with `columns: ['text']`.
 *
 * Tidied, and only this: line endings (CRLF, CR), a byte order mark, a tab (four spaces, since
 * the writer measures and draws spaces but has no tab stops), a form feed (a paragraph break),
 * spaces at the end of a line, and the other C0 control characters, which no font has a glyph
 * for and which a Notepad file can carry by accident.
 *
 * No loss entry and no diagnostic: nothing is lost going from plain text to a text PDF, so the
 * page has no loss list to show (section 7.2).
 *
 * PURE: no DOM, no worker. The bytes were decoded by the caller (`decodeBytes`, the same
 * detection and "Read as" override every subtitle tool uses).
 */

import type { Cue, ParseOptions, ParseResult, RunContext } from '../../../contracts/cue-model.ts';
import { MS_GRID, makeCue, makeLine, makeRun, originFor } from '../../core/model/build.ts';
import { TICKS_PER_SECOND } from '../../core/time/ticks.ts';
import { Draft, safeSpan } from '../common/draft.ts';

/** A tab is this many spaces. The writer has no tab stops, and a tab character has no glyph. */
const TAB = '    ';

/** C0 controls other than tab, line feed, carriage return and form feed, and DEL. */
// biome-ignore lint/suspicious/noControlCharactersInRegex: removing control characters is the point.
const CONTROL = /[\u0000-\u0008\u000B\u000E-\u001F\u007F]/g;

/** One paragraph: its lines, in order, each as the source has it (tidied as above). */
export type Paragraph = readonly string[];

/**
 * The text's paragraphs. A blank line, a line of only whitespace or a form feed ends one; every
 * other line break is kept as a line inside the paragraph. Empty text gives none.
 */
export function documentParagraphs(text: string): readonly Paragraph[] {
  const lines = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n').replace(/\f/g, '\n\n').split('\n');
  const out: string[][] = [];
  let current: string[] = [];
  for (const raw of lines) {
    const line = raw.replace(/\t/g, TAB).replace(CONTROL, '').trimEnd();
    if (line.trim() === '') {
      if (current.length > 0) out.push(current);
      current = [];
      continue;
    }
    current.push(line);
  }
  if (current.length > 0) out.push(current);
  return out;
}

/** How many characters the text has, as a person counts them: code points, line breaks too. */
export function characterCount(text: string): number {
  let n = 0;
  for (const _ of text.replace(/\r\n/g, '\n')) n++;
  return n;
}

/**
 * The text as a document: one cue per paragraph, one line per source line, on one track. The
 * same `ParseResult` a parser returns, so the worker stores it and the PDF writer reads it like
 * any other document. `format` on the result is 'txt', which is what the file is.
 */
export function parseTextDocument(text: string, opts: ParseOptions, ctx: RunContext): ParseResult {
  const draft = new Draft({ alloc: ctx.alloc, opts, sourceFormat: 'txt', grid: MS_GRID });
  let cursor = 0;
  documentParagraphs(text).forEach((paragraph, i) => {
    const id = ctx.alloc.cue();
    // A placeholder, one second per paragraph, in order. Never printed: the text-only layout
    // has no time column.
    const time = safeSpan(cursor, cursor + TICKS_PER_SECOND, draft, { cueId: id });
    cursor += TICKS_PER_SECOND;
    const cue = makeCue({
      id,
      time,
      lines: paragraph.map((line) => makeLine([makeRun({ id: ctx.alloc.run(), text: line })])),
      styleId: draft.defaultStyleId,
      trackId: draft.defaultTrackId,
      origin: originFor(draft.inputId, 'parse', i),
      granularity: 'coalesced',
    });
    draft.addCue(cue as Cue);
  });
  return draft.ok();
}
